import Box from "@mui/material/Box";
import { Tooltip, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { CompetitiveTiers } from "../../reckon-api";
import { useReferentialContext } from "../../Context";

export type RgbaColor = { r: number; g: number; b: number; a: number };

export function parseTierRgba(color?: string | null): RgbaColor | undefined {
    if (!color) return undefined;
    const trimmed = color.trim();
    if (trimmed.startsWith("rgb")) {
        const nums = trimmed.match(/[\d.]+/g)?.map(Number);
        if (!nums || nums.length < 3) return undefined;
        return { r: nums[0], g: nums[1], b: nums[2], a: nums[3] ?? 1 };
    }
    const hex = trimmed.replace(/^#|^0x/i, "");
    const expand = (h: string) => parseInt(h.length === 1 ? h + h : h, 16);
    if (/^[0-9a-fA-F]{8}$/.test(hex)) {
        return {
            r: expand(hex.slice(0, 2)),
            g: expand(hex.slice(2, 4)),
            b: expand(hex.slice(4, 6)),
            a: expand(hex.slice(6, 8)) / 255,
        };
    }
    if (/^[0-9a-fA-F]{6}$/.test(hex)) {
        return { r: expand(hex.slice(0, 2)), g: expand(hex.slice(2, 4)), b: expand(hex.slice(4, 6)), a: 1 };
    }
    if (/^[0-9a-fA-F]{3,4}$/.test(hex)) {
        return {
            r: expand(hex[0]),
            g: expand(hex[1]),
            b: expand(hex[2]),
            a: hex[3] ? expand(hex[3]) / 255 : 1,
        };
    }
    return undefined;
}

export function rgbaString(color: RgbaColor, alpha = color.a): string {
    return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
}

export function parseTierColor(color?: string | null): string | undefined {
    const parsed = parseTierRgba(color);
    if (!parsed) {
        const trimmed = color?.trim();
        if (trimmed?.startsWith("hsl")) return trimmed;
        return undefined;
    }
    const hex = (n: number) => n.toString(16).padStart(2, "0");
    return `#${hex(parsed.r)}${hex(parsed.g)}${hex(parsed.b)}`;
}

function usableFill(color?: RgbaColor): boolean {
    return Boolean(color && color.a > 0.02 && color.r + color.g + color.b > 12);
}

export function findCompetitiveTier(
    referential: CompetitiveTiers[],
    value?: number | string | null,
): CompetitiveTiers | undefined {
    if (value === null || value === undefined || value === "") return undefined;
    if (typeof value === "number" || /^\d+$/.test(String(value))) {
        const tier = Number(value);
        return referential.find((t) => t.tier === tier);
    }
    const needle = String(value).toLowerCase();
    return referential.find(
        (t) => t.id.toLowerCase() === needle || (t.division && t.division.toLowerCase() === needle),
    );
}

export function useCompetitiveTier(value?: number | string | null): CompetitiveTiers | undefined {
    const { competitiveTiersReferential } = useReferentialContext();
    return useMemo(
        () => findCompetitiveTier(competitiveTiersReferential, value),
        [competitiveTiersReferential, value],
    );
}

export function rankAccentColor(tier?: CompetitiveTiers, numericTier?: number | string | null): string {
    const parsed = parseTierRgba(tier?.color);
    if (parsed) return rgbaString(parsed, Math.max(parsed.a, 0.85));
    const n = typeof numericTier === "number" ? numericTier : Number(numericTier);
    if (numericTier == null || Number.isNaN(n) || n < 3) return "rgba(255,255,255,0.45)";
    if (n >= 27) return "#f5e6a8";
    if (n >= 24) return "#e57373";
    if (n >= 21) return "#81c784";
    if (n >= 18) return "#64b5f6";
    if (n >= 15) return "#4dd0e1";
    if (n >= 12) return "#ffd54f";
    if (n >= 9) return "#cfd8dc";
    if (n >= 6) return "#bcaaa4";
    return "#9e9e9e";
}

export function rankPalette(tier?: CompetitiveTiers, numericTier?: number | string | null) {
    const fg = parseTierRgba(tier?.color);
    const bg = parseTierRgba(tier?.background_color);
    const hasBg = usableFill(bg);
    const hasFg = usableFill(fg);

    return {
        accent: hasFg ? rgbaString(fg!, 1) : rankAccentColor(tier, numericTier),
        wash: hasBg ? rgbaString(bg!, 0.1) : hasFg ? rgbaString(fg!, 0.07) : "transparent",
    };
}

export function rankLabel(tier?: CompetitiveTiers): string {
    if (!tier) return "";
    const base = (tier.id && !/^\d+$/.test(tier.id) ? tier.id : tier.division) || "";
    if (!base) return fullRankFromNumber(tier.tier);
    if (/unranked/i.test(base)) return "Unranked";
    if (/radiant/i.test(base)) return "Radiant";
    if (/\d/.test(base)) return base;
    if (tier.tier >= 3 && tier.tier < 27) {
        return `${base} ${((tier.tier - 3) % 3) + 1}`;
    }
    return base;
}

export function fullRankFromNumber(n?: number | null): string {
    if (n == null || n < 3) return "Unranked";
    if (n >= 27) return "Radiant";
    const names = ["Iron", "Bronze", "Silver", "Gold", "Platinum", "Diamond", "Ascendant", "Immortal"];
    const idx = Math.floor((n - 3) / 3);
    if (idx < 0 || idx >= names.length) return "Radiant";
    return `${names[idx]} ${((n - 3) % 3) + 1}`;
}

const KEY_MATCH = 34;
const KEY_SOFT = 26;
const processedIconCache = new Map<string, Promise<string>>();

function pixelAt(data: Uint8ClampedArray, width: number, x: number, y: number): RgbaColor | undefined {
    if (x < 0 || y < 0) return undefined;
    const i = (y * width + x) * 4;
    if (i < 0 || i + 3 >= data.length) return undefined;
    if (data[i + 3] < 16) return undefined;
    return { r: data[i], g: data[i + 1], b: data[i + 2], a: data[i + 3] / 255 };
}

function colorDist(a: RgbaColor, b: RgbaColor): number {
    const dr = a.r - b.r;
    const dg = a.g - b.g;
    const db = a.b - b.b;
    return Math.sqrt(dr * dr + dg * dg + db * db);
}

function refineKey(data: Uint8ClampedArray, width: number, height: number, apiKey: RgbaColor): RgbaColor {
    const cx = width / 2;
    const cy = height / 2;
    const radius = Math.min(width, height) * 0.42;
    let r = 0;
    let g = 0;
    let b = 0;
    let n = 0;
    for (let s = 0; s < 12; s++) {
        const ang = (s / 12) * Math.PI * 2;
        const sample = pixelAt(
            data,
            width,
            Math.round(cx + Math.cos(ang) * radius),
            Math.round(cy + Math.sin(ang) * radius),
        );
        if (!sample || colorDist(sample, apiKey) > 50) continue;
        r += sample.r;
        g += sample.g;
        b += sample.b;
        n += 1;
    }
    if (n < 3) return apiKey;
    return { r: r / n, g: g / n, b: b / n, a: 1 };
}

function chromaKey(image: HTMLImageElement, apiKey: RgbaColor): string {
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx || canvas.width === 0 || canvas.height === 0) return image.src;

    ctx.drawImage(image, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    const key = refineKey(pixels, canvas.width, canvas.height, apiKey);

    for (let i = 0; i < pixels.length; i += 4) {
        if (pixels[i + 3] === 0) continue;
        const dist = colorDist({ r: pixels[i], g: pixels[i + 1], b: pixels[i + 2], a: 1 }, key);
        if (dist <= KEY_MATCH) {
            pixels[i + 3] = 0;
        } else if (dist < KEY_MATCH + KEY_SOFT) {
            pixels[i + 3] = Math.round(pixels[i + 3] * ((dist - KEY_MATCH) / KEY_SOFT));
        }
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL("image/png");
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error(`Failed to load rank icon: ${src}`));
        image.src = src;
    });
}

function transparentIcon(src: string, key: RgbaColor): Promise<string> {
    const cacheKey = `${src}|${Math.round(key.r)},${Math.round(key.g)},${Math.round(key.b)}`;
    const cached = processedIconCache.get(cacheKey);
    if (cached) return cached;

    const promise = loadImage(src)
        .then((image) => chromaKey(image, key))
        .catch(() => src);
    processedIconCache.set(cacheKey, promise);
    return promise;
}

export default function RankIcon(props: {
    tier?: number | string | null;
    rating?: number | null;
    size?: number;
    raw?: boolean;
    sx?: any;
}) {
    const rank = useCompetitiveTier(props.tier);
    const size = props.size || props.sx?.width || props.sx?.height || 40;
    const originalSrc = rank?.small_icon || rank?.large_icon || undefined;
    const name = rankLabel(rank) || fullRankFromNumber(typeof props.tier === "number" ? props.tier : Number(props.tier));
    const label =
        props.rating != null && !Number.isNaN(props.rating) ? `${name} · ${props.rating} RR` : name;
    const background = parseTierRgba(rank?.background_color);
    const [src, setSrc] = useState(originalSrc);

    useEffect(() => {
        setSrc(originalSrc);
        if (props.raw || !originalSrc || !background || !usableFill(background)) return;
        let cancelled = false;
        transparentIcon(originalSrc, background).then((next) => {
            if (!cancelled) setSrc(next);
        });
        return () => {
            cancelled = true;
        };
    }, [originalSrc, background?.r, background?.g, background?.b, props.raw]);

    if (!originalSrc) {
        if (!label) return null;
        return (
            <Tooltip title={label} followCursor={true}>
                <Typography
                    sx={{
                        fontWeight: 800,
                        fontSize: Math.max(10, size * 0.28),
                        whiteSpace: "nowrap",
                        ...props.sx,
                    }}
                >
                    {label}
                </Typography>
            </Tooltip>
        );
    }

    return (
        <Tooltip title={label} followCursor={true}>
            <Box
                component="img"
                src={src || originalSrc}
                alt={label}
                sx={{
                    width: size,
                    height: size,
                    minWidth: size,
                    minHeight: size,
                    objectFit: "contain",
                    ...props.sx,
                }}
            />
        </Tooltip>
    );
}
